import { useEffect, useState } from "react";
import { AlertCircle, Archive, X } from "lucide-react";
import { auth } from "../firebase";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { MELT_BUTTON, MELT_BUTTON_QUIET, MELT_DIALOG, MELT_INPUT, MELT_LABEL, MELT_REQUIRED } from "./ui/melt";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

export type ArchiveSummary = {
  count: number;
  byStage: Record<string, number>;
  reached: { oa: number; interview: number; offer: number };
  responseRate: number;
  firstApplicationDate: string | null;
  lastApplicationDate: string | null;
};

export type ArchiveResult = { id: string; name: string; summary: ArchiveSummary };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onArchived: (result: ArchiveResult) => void;
};

// "2026-01-10" -> "Jan 10, 2026", read as a local date so it doesn't shift a day.
function formatDay(iso: string | null) {
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error("Please log in again to continue.");
  return { Authorization: `Bearer ${await user.getIdToken()}` };
}

export default function ArchiveCycleModal({ isOpen, onClose, onArchived }: Props) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<ArchiveSummary | null>(null);
  const [name, setName] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSummary(null);

    (async () => {
      try {
        const r = await fetch(`${API_BASE}/archives/preview`, { headers: await authHeaders() });
        const json = await r.json();
        if (!r.ok) throw new Error(json?.error || "Failed to load your applications");
        if (cancelled) return;
        setSummary(json.summary);
        setName(json.suggestedName || "");
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load your applications");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleArchive = async () => {
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch(`${API_BASE}/archives`, {
        method: "POST",
        headers: { ...(await authHeaders()), "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      const json = await r.json();
      if (json?.error === "NOTHING_TO_ARCHIVE") throw new Error("Your tracker is already empty.");
      if (json?.error === "INVALID_NAME") throw new Error("Give the archive a name (80 characters max).");
      if (!r.ok) throw new Error(json?.error || "Failed to archive");
      onArchived(json);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? "Failed to archive");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const count = summary?.count ?? 0;
  const first = formatDay(summary?.firstApplicationDate ?? null);
  const last = formatDay(summary?.lastApplicationDate ?? null);
  const stats = summary
    ? [
        { label: "Applications", value: summary.count },
        { label: "Interviews", value: summary.reached.interview },
        { label: "Offers", value: summary.reached.offer },
        { label: "Response rate", value: `${Math.round(summary.responseRate * 100)}%` },
      ]
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent showClose={false} className={MELT_DIALOG}>
        <div className="flex items-start justify-between gap-4 px-7 pt-6">
          <div>
            <DialogTitle className="text-[22px] font-semibold tracking-[-.02em] text-melt-text1">
              Archive current cycle
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-melt-text2">Save this cycle and start fresh.</DialogDescription>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="-mr-2 grid h-8 w-8 flex-none place-items-center rounded-lg text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex flex-col gap-5 px-7 pb-6 pt-5">
          {loading ? (
            <p className="font-plex text-xs text-melt-text3">Loading your applications…</p>
          ) : count === 0 && !error ? (
            <p className="text-sm text-melt-text2">
              Nothing to archive. Your tracker doesn't have any applications right now.
            </p>
          ) : (
            summary && (
              <>
                <div className="flex flex-col gap-2">
                  <label htmlFor="archive-name" className={MELT_LABEL}>Archive name<span aria-hidden className={MELT_REQUIRED}>*</span></label>
                  <input
                    id="archive-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    placeholder="e.g., Summer 2027 Recruiting"
                    className={MELT_INPUT}
                  />
                </div>

                <div>
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                    {stats.map((s) => (
                      <div key={s.label} className="rounded-xl border border-melt-hair bg-white px-3 py-3">
                        <p className="font-plex text-lg font-medium text-melt-text1">{s.value}</p>
                        <p className="text-xs text-melt-text3">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {first && last && (
                    <p className="mt-2 font-plex text-xs text-melt-text3">
                      Applied {first === last ? `on ${first}` : `${first} – ${last}`}
                    </p>
                  )}
                </div>

                <div className="flex items-start gap-3 rounded-xl bg-melt-peach px-4 py-3.5">
                  <AlertCircle size={17} strokeWidth={1.75} className="mt-0.5 flex-none text-melt-amber" />
                  <p className="text-sm leading-relaxed text-melt-amber">
                    Your tracker will be cleared. These {count} applications will move to{" "}
                    <strong>Archives</strong> in Settings, and new syncs will start fresh cards.
                  </p>
                </div>
              </>
            )
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-[10px] bg-[#F7E6DE] px-4 py-3 text-sm text-[#9A4F33]">
              <AlertCircle size={16} strokeWidth={1.75} />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={submitting}
              className={MELT_BUTTON_QUIET}
            >
              Cancel
            </button>
            <button
              onClick={handleArchive}
              disabled={loading || submitting || count === 0 || !name.trim()}
              className={MELT_BUTTON}
            >
              <Archive size={16} strokeWidth={1.75} />
              {submitting ? "Archiving…" : `Archive ${count} application${count === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
